'use client'

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Laptop, Loader2, RefreshCcw, Smartphone, X } from 'lucide-react'

import type { Locale } from '@helper/utils/types'
import { Badge, Button, Skeleton } from '@ui/web/components'
import cn from '@config/tailwind/cn'

import { useAuthClient, useSharedTRPC } from '../context'

interface SessionsListProps {
  className?: string
  locale?: Locale
}

export function SessionsList({ className, locale = 'id' }: SessionsListProps) {
  const trpc = useSharedTRPC()
  const queryClient = useQueryClient()
  const authClient = useAuthClient()

  const revokeSessionMutation = useMutation(
    trpc.session.revokeSession.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: trpc.session.listAllSessions.queryKey() })
      },
    }),
  )

  const {
    data: activeOrganization,
    isPending: isPendingActiveOrganization,
    error: errorActiveOrganization,
    refetch: refetchActiveOrganization,
  } = authClient.useActiveOrganization()

  const {
    data: currentSession,
    isPending: isPendingCurrentSession,
    error: errorCurrentSession,
    refetch: refetchCurrentSession,
  } = authClient.useSession()

  const {
    data: listAllSessions,
    isPending: isPendingListAllSessions,
    refetch: refetchListAllSessions,
    isError: isErrorListAllSessions,
  } = useQuery({ ...trpc.session.listAllSessions.queryOptions(), staleTime: 1000 * 60 * 5 })

  const isPending = isPendingActiveOrganization || isPendingListAllSessions || isPendingCurrentSession

  const isError = errorActiveOrganization !== null || isErrorListAllSessions || errorCurrentSession

  if (isPending) {
    return (
      <section className={cn('srd:flex srd:flex-col srd:gap-y-8 srd:w-full', className)}>
        <div className='srd:flex srd:flex-col srd:gap-2'>
          <h6 className='srd:text-lg srd:font-medium'>{locale === 'id' ? 'Perangkat aktif' : 'Active devices'}</h6>
          <p className='srd:text-sm'>
            {locale === 'id'
              ? 'Perangkat yang sudah aktif menggunakan akunmu'
              : 'Devices that are already active using your account'}
          </p>
        </div>

        <section className='srd:w-full srd:flex srd:flex-col srd:gap-8'>
          {new Array(2).fill(0).map((_, index) => {
            return (
              <section
                key={`active-device-loading-${index}`}
                className='srd:w-full srd:h-fit srd:flex srd:items-center srd:justify-between'
              >
                <section className='srd:flex srd:items-center srd:justify-center srd:w-fit srd:h-fit srd:gap-x-4'>
                  <Skeleton className='srd:w-16 srd:h-16 srd:rounded-2xl' />

                  <section className='srd:flex srd:flex-col srd:gap-1'>
                    <section className='srd:flex srd:items-center srd:justify-center srd:w-fit srd:h-fit srd:gap-x-2'>
                      <Skeleton className='srd:w-[90px] srd:h-[24px] srd:rounded-full' />
                    </section>
                    <section className='srd:flex srd:flex-col srd:gap-1 srd:text-muted-foreground srd:text-sm'>
                      <Skeleton className='srd:w-[180px] srd:h-[20px] srd:rounded-full' />
                      <Skeleton className='srd:w-[180px] srd:h-[20px] srd:rounded-full' />
                    </section>
                  </section>
                </section>

                <Skeleton className='srd:w-[90px] srd:h-[40px] srd:rounded-xl' />
              </section>
            )
          })}
        </section>
      </section>
    )
  }

  if (isError) {
    return (
      <section className={cn('srd:flex srd:flex-col srd:gap-y-8', className)}>
        <div className='srd:flex srd:flex-col srd:gap-2'>
          <h6 className='srd:text-lg srd:font-medium'>{locale === 'id' ? 'Perangkat aktif' : 'Active devices'} </h6>
          <p className='srd:text-sm'>
            {locale === 'id'
              ? 'Perangkat yang sudah aktif menggunakan akunmu'
              : 'Devices that are already active using your account'}
          </p>
        </div>

        <section className='srd:w-full srd:flex srd:flex-col srd:gap-2 srd:items-center srd:justify-center'>
          <p className='srd:w-full srd:text-left srd:text-error srd:flex srd:items-center'>
            <X className='srd:w-4 srd:h-4 srd:mr-2' />{' '}
            {locale === 'id'
              ? 'Ada error yang terjadi saat mengambil data perangkat aktif'
              : 'An error occurred while fetching active device data'}
          </p>

          <Button
            variant='outline'
            onClick={async () => {
              await refetchActiveOrganization()
              await refetchListAllSessions()
              await refetchCurrentSession()
            }}
          >
            <RefreshCcw className='srd:size-4 srd:mr-2 srd:text-blue-100' />
            {locale === 'id' ? 'Coba lagi' : 'Try again'}
          </Button>
        </section>
      </section>
    )
  }

  const data = listAllSessions

  return (
    <div className={cn('srd:flex srd:flex-col srd:gap-y-8', className)}>
      <div className='srd:flex srd:flex-col srd:gap-2'>
        <h6 className='srd:text-lg srd:font-medium'>{locale === 'id' ? 'Perangkat aktif' : 'Active devices'}</h6>
        <p className='srd:text-sm'>
          {locale === 'id'
            ? 'Perangkat yang sudah aktif menggunakan akunmu'
            : 'Devices that are already active using your account'}
        </p>
      </div>

      <section className='srd:w-full srd:flex srd:flex-col srd:gap-8'>
        {data
          .filter((session) => session.activeOrganizationId === activeOrganization?.id)
          .sort((a, b) => {
            const aIsCurrent = a.id === currentSession?.session.id
            const bIsCurrent = b.id === currentSession?.session.id
            if (aIsCurrent && !bIsCurrent) return -1
            if (!aIsCurrent && bIsCurrent) return 1
            return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          })
          .map((session) => {
            const deviceLabel =
              session.deviceType === 'mobile' ? 'Mobile' : session.deviceType === 'tablet' ? 'Tablet' : 'Desktop'

            const locationText =
              session.ipAddress && (session.city ?? session.country)
                ? `${session.ipAddress} (${[session.city, session.country].filter(Boolean).join(', ')})`
                : (session.ipAddress ?? '—')

            const isImpersonated = session.impersonatedBy !== null

            const isCurrentSession = session.id === currentSession?.session.id

            return (
              <section key={session.id} className='srd:w-full srd:h-fit srd:flex srd:items-center srd:justify-between'>
                <section className='srd:flex srd:items-center srd:justify-center srd:w-fit srd:h-fit srd:gap-x-4'>
                  {session.deviceType === 'mobile' ? (
                    <Smartphone className='srd:w-12 srd:h-16 srd:shrink-0' />
                  ) : (
                    <Laptop className='srd:w-12 srd:h-12 srd:shrink-0' />
                  )}

                  <section className='srd:flex srd:flex-col srd:gap-0'>
                    <section className='srd:flex srd:items-center srd:justify-center srd:w-fit srd:h-fit srd:gap-x-2'>
                      <p className='srd:font-medium'>{deviceLabel}</p>

                      {isCurrentSession && (
                        <Badge className='srd:bg-purple-10 srd:text-purple-100 srd:hover:bg-purple-100 srd:hover:text-neutral-white'>
                          {locale === 'id' ? 'Perangkat ini' : 'This device'}
                        </Badge>
                      )}

                      {isImpersonated && <Badge>Tim PiBo</Badge>}
                    </section>
                    <section className='srd:min-w-0 srd:flex srd:flex-col srd:gap-0 srd:text-navy-100 srd:text-sm'>
                      <p>
                        {session.browserName}
                        {session.browserVersion ? ` ${session.browserVersion}` : ''}
                      </p>
                      <p className='srd:min-w-0 srd:max-w-[200px] srd:overflow-hidden srd:text-ellipsis srd:whitespace-nowrap'>
                        {locationText}
                      </p>
                    </section>
                  </section>
                </section>

                {!isCurrentSession && (
                  <Button
                    variant='destructive'
                    disabled={revokeSessionMutation.isPending}
                    onClick={() => revokeSessionMutation.mutate({ sessionId: session.id })}
                    className='srd:p-0 srd:px-3 srd:py-0.5 srd:text-xs'
                  >
                    {revokeSessionMutation.isPending && revokeSessionMutation.variables.sessionId === session.id ? (
                      <Loader2 className='srd:size-4 srd:animate-spin' />
                    ) : (
                      <span className='srd:whitespace-nowrap srd:text-ellipsis'>
                        {locale === 'id' ? 'Akhirkan sesi' : 'End session'}
                      </span>
                    )}
                  </Button>
                )}
              </section>
            )
          })}
      </section>
    </div>
  )
}
